import { Typography } from "@mui/material";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { hasViewAccessToResource } from "api/general";
import { useTiers } from "api/tiers";
import { Subheader } from "components/Header/Subheader/Subheader";
import { SubheaderLinkButton } from "components/Header/Subheader/SubheaderLinkButton";
import { SubheaderWrapper } from "components/Header/Subheader/SubheaderWrapper";
import { Breadcrumbs, TopHeader } from "components/Header/TopHeader/TopHeader";
import { ReactComponent as Settings01Icon } from "components/Icons/settings-01.svg";
import { LoadingInterceptor } from "components/LoadingInterceptor/LoadingInterceptor";
import { StatusValue } from "components/Table/StatusValue";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { useMessage } from "hooks/intl";
import { useParams } from "react-router-dom";
import { links } from "utilities/links";
import { BasicSettings } from "./BasicSettings";
import { EmptyTierSet } from "./EmptyTierSet";
import { TierSetTiers } from "./TierSetTiers/TierSetTiers";

const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    title: {
      fontSize: "16px",
      fontWeight: 500,
      margin: theme.spacing(12, 0, 8),
    },
  }),
);

export const TierSet = () => {
  const classes = useStyles();
  const { message } = useMessage();
  const { id: tierSetId } = useParams<{ id: string }>();
  const hasViewTierPermission = hasViewAccessToResource("LEVEL");
  const { useTierSetData, useTierSetTiers } = useTiers();
  const { data: tierSetData } = useTierSetData(tierSetId);
  const { data: tierSetTiers } = useTierSetTiers(tierSetId, {
    filters: [
      {
        condition: "sortOrder",
        query: "_orderBy",
        value: "asc",
      },
    ],
  });

  const isLoading = !tierSetData || !tierSetTiers;

  // Omit default Tier in Tierset
  const isAnyTierAdded = tierSetTiers && tierSetTiers.items.length > 1;

  const formatedTiers =
    tierSetData && tierSetTiers
      ? tierSetTiers.items.map((tier) => {
          return {
            ...tier,
            conditions: tier.conditions.map((condition) => {
              const matchedCondition = tierSetData.conditions.find(
                ({ id }) => id === condition.conditionId,
              );

              return {
                attribute: matchedCondition?.attribute || "",
                value: condition.value,
                walletType: matchedCondition?.walletType,
                eventName: matchedCondition?.eventName,
              };
            }),
          };
        })
      : [];

  const breadcrumbs: Breadcrumbs = {
    parent: {
      label: message({ id: "listOfTierSets" }),
      path: links.tierSetList,
    },
  };

  return (
    <LoadingInterceptor isLoading={isLoading}>
      <>
        <TopHeader
          title={message({ id: "tiersSet" })}
          clearLink={links.tierSetList}
          badge={<StatusValue status={!!tierSetData?.active} />}
          breadcrumbs={breadcrumbs}
        />
        <Subheader>
          <SubheaderWrapper alignRight>
            <SubheaderLinkButton
              text={message({ id: "configuration" })}
              link={links.editTierSet(tierSetId)}
              icon={<Settings01Icon />}
              disabled={hasViewTierPermission}
              variant="outlined"
              data-test-id="configuration"
            />
            <SubheaderLinkButton
              text={
                isAnyTierAdded
                  ? message({ id: "editTiers" })
                  : message({ id: "addTiers" })
              }
              link={
                isAnyTierAdded
                  ? links.tierSetEditTiers(tierSetId)
                  : links.tierSetAddTiers(tierSetId)
              }
              disabled={hasViewTierPermission}
              data-test-id={
                isAnyTierAdded
                  ? "tiers-set-edit-tiers-button"
                  : "tier-set-add-tiers-button"
              }
            />
          </SubheaderWrapper>
        </Subheader>
        {isAnyTierAdded && tierSetData && tierSetTiers ? (
          <>
            <Typography className={classes.title}>
              {message({ id: "tierSetSettings" })}
            </Typography>
            <BasicSettings
              name={tierSetData.name}
              description={tierSetData.description}
              labels={tierSetData.labels}
            />
            <Typography mt={12} className={classes.title}>
              {message({ id: "tiers" })}
            </Typography>
            <TierSetTiers tiers={formatedTiers} />
          </>
        ) : (
          <EmptyTierSet tierSetId={tierSetData?.tierSetId} />
        )}
      </>
    </LoadingInterceptor>
  );
};
